import React, { useState } from 'react';
import { captureLead } from '../lib/api';
import { getAttribution } from '../lib/attribution';

export default function LeadForm({ funnelId, pageId, ctaId, fields, branding, onSuccess }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const primaryColor = branding?.primary_color || '#1e40af';
  const accentColor = branding?.secondary_color || '#AEFFE3';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const attribution = getAttribution();
      const result = await captureLead(funnelId, pageId, ctaId, formData, attribution);

      if (onSuccess) {
        onSuccess(result);
      } else {
        if (result.next_page) {
          window.location.href = result.next_page;
        } else {
          alert('Thank you! We will be in touch soon.');
        }
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const baseClasses = "w-full px-4 py-3 bg-white/90 border border-gray-300/50 rounded-xl focus:outline-none focus:ring-2 focus:border-transparent backdrop-blur-sm transition-all";

    switch (field.type) {
      case 'select':
        return (
          <select
            name={field.name}
            required={field.required}
            onChange={handleChange}
            className={baseClasses}
            style={{ focusRing: primaryColor }}
          >
            <option value="">Select...</option>
            {field.options?.map((opt, idx) => (
              <option key={idx} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'textarea':
        return (
          <textarea
            name={field.name}
            required={field.required}
            onChange={handleChange}
            rows={4}
            placeholder={field.placeholder}
            className={baseClasses}
            style={{ focusRing: primaryColor }}
          />
        );

      case 'radio':
        return (
          <div className="space-y-3">
            {field.options?.map((opt, idx) => (
              <label key={idx} className="flex items-center space-x-3 p-3 bg-white/50 backdrop-blur-sm rounded-xl border border-gray-200/50 hover:bg-white/80 transition-all cursor-pointer">
                <input
                  type="radio"
                  name={field.name}
                  value={opt}
                  required={field.required}
                  onChange={handleChange}
                  className="w-5 h-5"
                  style={{ accentColor: primaryColor }}
                />
                <span className="text-gray-700 font-medium">{opt}</span>
              </label>
            ))}
          </div>
        );

      default:
        return (
          <input
            type={field.type || 'text'}
            name={field.name}
            required={field.required}
            onChange={handleChange}
            placeholder={field.placeholder}
            className={baseClasses}
            style={{ focusRing: primaryColor }}
          />
        );
    }
  };

  return (
    <section className="relative bg-gradient-to-b from-white via-blue-50/30 to-white py-24 px-6 overflow-hidden">
      {/* Background gradients */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl" />
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl opacity-20"
          style={{ backgroundColor: accentColor }}
        />
      </div>

      <div className="max-w-3xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-50 rounded-full border border-blue-200">
            <span className="text-sm font-semibold text-blue-900">
              Free Assessment • No Obligation
            </span>
          </div>
          <h2 className="text-5xl font-bold mb-4" style={{ color: primaryColor }}>
            Get Your Tax Strategy Assessment
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Takes 2 minutes • Receive your personalized strategy within 24 hours
          </p>
        </div>

        {/* Glass morphism form card */}
        <div className="relative group">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-3xl blur-2xl opacity-50 group-hover:opacity-75 transition-opacity" />

          {/* Form card */}
          <div className="relative bg-white/80 backdrop-blur-2xl rounded-3xl p-8 md:p-12 border border-white/50 shadow-2xl">
            {error && (
              <div className="mb-6 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200/50 rounded-xl text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {fields.map((field, idx) => (
                <div key={idx}>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    {field.label || field.name}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                </div>
              ))}

              <button
                type="submit"
                disabled={loading}
                className="group relative w-full px-8 py-4 rounded-xl font-bold text-lg text-white shadow-2xl overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:scale-[1.02]"
                style={{ backgroundColor: primaryColor }}
              >
                <span className="relative z-10">
                  {loading ? 'Submitting...' : 'Get My Free Assessment'}
                </span>
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: `linear-gradient(135deg, ${accentColor}40, transparent)` }}
                />
              </button>
            </form>

            {/* Trust elements */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center justify-center gap-6 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <span>Secure & Private</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>No Spam Ever</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span>Free Forever</span>
                </div>
              </div>

              <p className="text-center text-xs text-gray-500">
                By submitting, you agree to receive communications from Crown Tax Center. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ under form */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 border border-gray-200/50">
            <h3 className="font-semibold text-gray-900 mb-2">What happens after I submit?</h3>
            <p className="text-sm text-gray-600">
              Our tax strategists will review your information and contact you within 24 hours to schedule a free consultation.
            </p>
          </div>
          <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 border border-gray-200/50">
            <h3 className="font-semibold text-gray-900 mb-2">Is there really no obligation?</h3>
            <p className="text-sm text-gray-600">
              Absolutely. The assessment is 100% free with no pressure to buy. Many clients use our strategies with their existing CPA.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
